/**
 * Privacy Policy dialog component.
 *
 * GDPR-compliant privacy policy displayed in a dialog overlay.
 */

import { APP_NAME, DEVELOPER_NAME } from '@idle-depths/shared';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PrivacyDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * Privacy Policy dialog.
 */
export function PrivacyDialog({ open, onClose }: PrivacyDialogProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl">Privacy Policy</DialogTitle>
          <DialogDescription>Last updated: January 9, 2026</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)] px-6 pb-6">
          <div className="space-y-6 text-sm text-foreground/90">
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Introduction</h2>
              <p className="leading-relaxed">
                {DEVELOPER_NAME} (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) operates{' '}
                {APP_NAME} (the &quot;Game&quot;). This Privacy Policy explains how we collect, use,
                and protect your personal information when you use our Game.
              </p>
              <p className="leading-relaxed">
                We are committed to protecting your privacy and complying with the General Data
                Protection Regulation (GDPR) and other applicable data protection laws.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Information We Collect</h2>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-foreground">Account Information</h3>
                <p className="leading-relaxed">When you sign in with Google, we collect:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                  <li>Email address</li>
                  <li>Display name</li>
                  <li>Profile picture URL</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-foreground">Game Data</h3>
                <p className="leading-relaxed">When you play the Game, we store:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                  <li>Game progress and character data</li>
                  <li>User preferences and settings</li>
                  <li>Purchase history and entitlements</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-foreground">Technical Data</h3>
                <p className="leading-relaxed">We automatically collect:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                  <li>IP address (for session security)</li>
                  <li>Browser/device information</li>
                  <li>Error reports and performance data (via Sentry)</li>
                  <li>Basic analytics (if you consent)</li>
                </ul>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                How We Use Your Information
              </h2>
              <p className="leading-relaxed">We use your information to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Provide and maintain the Game</li>
                <li>Save and sync your game progress</li>
                <li>Process purchases and manage entitlements</li>
                <li>Improve the Game and fix bugs</li>
                <li>Send important updates (with your consent)</li>
                <li>Prevent fraud and ensure security</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">
                Data Storage and Security
              </h2>
              <p className="leading-relaxed">
                Your data is stored securely using Cloudflare&apos;s infrastructure in the United
                States. We implement appropriate technical and organizational measures to protect
                your personal data, including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Encryption in transit (HTTPS/TLS)</li>
                <li>Secure session management</li>
                <li>Access controls and authentication</li>
                <li>Regular security reviews</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Third-Party Services</h2>
              <p className="leading-relaxed">We use the following third-party services:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>
                  <span className="text-foreground font-medium">Google OAuth</span> - Authentication
                </li>
                <li>
                  <span className="text-foreground font-medium">Cloudflare</span> - Hosting and
                  infrastructure
                </li>
                <li>
                  <span className="text-foreground font-medium">Sentry</span> - Error tracking and
                  monitoring
                </li>
                <li>
                  <span className="text-foreground font-medium">BetterStack</span> - Logging
                  (server-side only)
                </li>
              </ul>
              <p className="leading-relaxed">
                Each of these services has their own privacy policies. We encourage you to review
                them.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Your Rights (GDPR)</h2>
              <p className="leading-relaxed">Under the GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>
                  <span className="text-foreground font-medium">Right to Access</span> - Request a
                  copy of your personal data
                </li>
                <li>
                  <span className="text-foreground font-medium">Right to Rectification</span> -
                  Request correction of inaccurate data
                </li>
                <li>
                  <span className="text-foreground font-medium">Right to Erasure</span> - Request
                  deletion of your data
                </li>
                <li>
                  <span className="text-foreground font-medium">Right to Data Portability</span> -
                  Export your data in a machine-readable format
                </li>
                <li>
                  <span className="text-foreground font-medium">Right to Withdraw Consent</span> -
                  Withdraw consent at any time
                </li>
              </ul>
              <p className="leading-relaxed">
                You can exercise these rights through your Account Settings page or by contacting
                us.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Data Retention</h2>
              <p className="leading-relaxed">
                We retain your data for as long as your account is active. When you delete your
                account, all associated data is permanently deleted immediately.
              </p>
              <p className="leading-relaxed">
                Guest data is stored only in your browser&apos;s local storage and is not retained
                on our servers.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Children&apos;s Privacy</h2>
              <p className="leading-relaxed">
                The Game is not intended for children under 13 years of age. We do not knowingly
                collect personal information from children under 13.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                significant changes by posting the new policy on this page and updating the
                &quot;Last updated&quot; date.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-biolume-cyan">Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or your personal data, please
                contact us at:
              </p>
              <p>
                Email:{' '}
                <a
                  href="mailto:support@fireknotstudios.com"
                  className="text-biolume-cyan hover:underline"
                >
                  support@fireknotstudios.com
                </a>
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
